import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Label, Input, Accordion } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import { StripeAccountSelector } from "./StripeAccountSelector";
import { ProductSearchRow } from "./ProductSearchRow";
import { SelectedProductRow } from "./SelectedProductRow";
import { useShopBlockEditorLogic } from "./useShopBlockEditorLogic";
import type { SelectedShopEntry } from "./types";
import type { ShopProduct } from "../enduser/types";

export interface ShopBlockEditorProps {
  title: string | undefined;
  subtitle: string | undefined;
  emptyMessage: string | undefined;
  confirmationMessage: string | undefined;
  products: ShopProduct[];
  stripeKey: string | undefined;
  onSave: (props: {
    title: string | undefined;
    subtitle: string | undefined;
    emptyMessage: string | undefined;
    confirmationMessage: string | undefined;
    products: ShopProduct[];
    stripeKey: string | undefined;
  }) => void;
}

const toSelectedShopEntry = (product: ShopProduct): SelectedShopEntry => ({
  stripeProductId: product.stripeProductId,
  title: product.title,
  description: product.description,
  image: product.image,
  formId: product.formId,
  variants: product.variants,
  badge: product.badge,
  recommended: product.recommended,
  category: product.category,
});

export const ShopBlockEditor = ({
  title,
  subtitle,
  emptyMessage,
  confirmationMessage,
  products,
  stripeKey,
  onSave,
}: ShopBlockEditorProps) => {
  const { t } = useTranslation("shop");
  const [titleInput, setTitleInput] = useState(title ?? "");
  const [subtitleInput, setSubtitleInput] = useState(subtitle ?? "");
  const [emptyMessageInput, setEmptyMessageInput] = useState(emptyMessage ?? "");
  const [confirmationMessageInput, setConfirmationMessageInput] = useState(confirmationMessage ?? "");
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);

  const logic = useShopBlockEditorLogic({
    initialSelection: products.map(toSelectedShopEntry),
    initialStripeKey: stripeKey ?? null,
  });

  const handleExpandedChange = (value: string) => {
    const next = value || null;
    setExpandedProductId(next);
    logic.setDrillDownProductId(next);
  };

  const handleDragStart = (stripeProductId: string) => setDraggedProductId(stripeProductId);

  const handleDragOver = (e: React.DragEvent, targetStripeProductId: string) => {
    e.preventDefault();
    if (!draggedProductId || draggedProductId === targetStripeProductId) return;

    const fromIndex = logic.selection.findIndex((entry) => entry.stripeProductId === draggedProductId);
    const toIndex = logic.selection.findIndex((entry) => entry.stripeProductId === targetStripeProductId);
    if (fromIndex === -1 || toIndex === -1) return;

    logic.reorderSelection(fromIndex, toIndex);
  };

  const handleDragEnd = () => setDraggedProductId(null);

  const handleSave = () => {
    const snapshot = logic.buildSnapshot();
    if (!snapshot) return;
    onSave({
      title: titleInput || undefined,
      subtitle: subtitleInput || undefined,
      emptyMessage: emptyMessageInput || undefined,
      confirmationMessage: confirmationMessageInput || undefined,
      products: snapshot,
      stripeKey: logic.stripeKey ?? undefined,
    });
    // This only stages the selection into the in-memory schema draft (updateProps in
    // ShopBlockEditorAdapter) — there's no request and no error path, unlike every other "Save"
    // in the admin dashboard, which persists to the server. Without this toast the click has no
    // visible effect at all, since the button's own appearance doesn't change either way.
    toast.success(t("Product selection staged — save the portal configuration to publish it"));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Heading (optional)")}</Label>
        <Input value={titleInput} onChange={(e) => setTitleInput(e.target.value)} placeholder={t("All Products")} />
      </div>

      <div className="space-y-2">
        <Label>{t("Subtitle (optional)")}</Label>
        <Input
          value={subtitleInput}
          onChange={(e) => setSubtitleInput(e.target.value)}
          placeholder={t("Browse our curated selection of medications and supplements")}
        />
      </div>

      {logic.selection.length > 0 && (
        <div className="space-y-2">
          <Label>{t("Selected Products")}</Label>
          <div className="space-y-1.5">
            {logic.selection.map((entry) => (
              <SelectedProductRow
                key={entry.stripeProductId}
                entry={entry}
                isVariantResolved={(stripePriceId) =>
                  // Adding/removing ANY price re-keys the whole reverse-lookup query, so gating on
                  // one aggregate "has the current fetch settled" flag would suppress every row's
                  // answer whenever a sibling row changes — including rows whose answer was already
                  // known, which flickered their warning off and back on instead of the newly-added
                  // row's. hasCheckedPriceId answers per price ID from an accumulator that persists
                  // known answers across query-key changes, so only a genuinely new, never-before-
                  // checked price ID defers to "assume resolved" here.
                  !logic.hasCheckedPriceId(stripePriceId) || logic.productByStripePriceId.has(stripePriceId)
                }
                isDragging={draggedProductId === entry.stripeProductId}
                onDragStart={() => handleDragStart(entry.stripeProductId)}
                onDragOver={(e) => handleDragOver(e, entry.stripeProductId)}
                onDragEnd={handleDragEnd}
                onRemove={() => logic.removeSelection(entry.stripeProductId)}
                onBadgeChange={(badge) => logic.setEntryBadge(entry.stripeProductId, badge)}
                onRecommendedChange={(recommended) => logic.setEntryRecommended(entry.stripeProductId, recommended)}
                onCategoryChange={(category) => logic.setEntryCategory(entry.stripeProductId, category)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2 pt-2 border-t">
        <Label>{t("Add Products")}</Label>
        <StripeAccountSelector
          accounts={logic.accounts}
          isLoading={logic.accountsLoading}
          value={logic.stripeKey}
          onChange={logic.setStripeKey}
        />

        {logic.stripeKey && (
          <>
            <Input
              value={logic.productsQuery.search}
              onChange={(e) => logic.productsQuery.setSearch(e.target.value)}
              placeholder={t("Search products…")}
            />

            {logic.productsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground py-2">{t("Loading products…")}</p>
            ) : logic.productsQuery.error ? (
              <p className="text-sm text-destructive py-2">
                {t("Couldn't load products from Stripe. Please try again.")}
              </p>
            ) : logic.productsQuery.products.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">{t("No products found in Stripe.")}</p>
            ) : (
              <Accordion
                type="single"
                collapsible
                value={expandedProductId ?? ""}
                onValueChange={handleExpandedChange}
                className="space-y-1.5"
              >
                {logic.productsQuery.products.map((product) => (
                  <ProductSearchRow
                    key={product.id}
                    product={product}
                    prices={expandedProductId === product.id ? logic.pricesQuery.prices : []}
                    pricesLoading={expandedProductId === product.id && logic.pricesQuery.isLoading}
                    isPriceSelected={logic.isPriceSelected}
                    onTogglePrice={logic.togglePrice}
                  />
                ))}
              </Accordion>
            )}

            {logic.productsQuery.canLoadMore && (
              <Button variant="outline" size="sm" onClick={logic.productsQuery.loadMore}>
                {t("Load more")}
              </Button>
            )}
          </>
        )}
      </div>

      <div className="space-y-2 pt-2 border-t">
        {/* Shared across every block editor's empty-state field — stays in the common namespace, not shop-specific. */}
        <Label>{t("Empty Message (optional)", { ns: "common" })}</Label>
        <Input
          value={emptyMessageInput}
          onChange={(e) => setEmptyMessageInput(e.target.value)}
          placeholder={t("No products available")}
        />
      </div>

      <div className="space-y-2">
        <Label>{t("Order Confirmation Message (optional)")}</Label>
        <Input
          value={confirmationMessageInput}
          onChange={(e) => setConfirmationMessageInput(e.target.value)}
          placeholder={t("Your order has been placed.")}
        />
      </div>

      {/* "Save" is shared across every block editor's save button — stays in the common namespace, not shop-specific. */}
      <Button onClick={handleSave} disabled={!logic.canSave} loading={logic.resolutionLoading}>
        {t("Save", { ns: "common" })}
      </Button>
      <p className="text-xs text-muted-foreground">
        {t(
          "This updates the block's product selection in your draft. It won't appear for endusers until you also save the portal configuration."
        )}
      </p>
      {logic.unresolvedSelections.length > 0 && (
        <p className="text-xs text-destructive">{t("Resolve the unconfigured product(s) above before saving.")}</p>
      )}
    </div>
  );
};
